/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

// tslint:disable:variable-name

import { EntityVersion, ID, WithRecalculationNeeded, WithVersion } from '@gh/common-shared-data';
import { createEnum, EnumSet, WithOptimisticLock } from '@gh/core-data';
import { isNotNil } from '@gh/core-util';

import { isEmpty, isNil } from 'lodash';

import { JobContractDaysPeriodType } from '../system-vars/system-vars.dto';
import { UserSystemRole } from '../users';

export type ScheduleTemplateListFilter = {
  type?: ScheduleType;
  showDeleted?: boolean;
  searchString?: string;
  showConstructionTemplates?: boolean;
  showSalesTemplates?: boolean;
  showPreConstructionsTemplates?: boolean;
  showMaintenanceTemplates?: boolean;
};

export const ScheduleTemplateStatus = createEnum('SCH_TEMPLATE_STATUS', {
  Active: 1,
  Deleted: 0,
});
export type ScheduleTemplateStatus = EnumSet<typeof ScheduleTemplateStatus>;

export const TaskRelationType = createEnum('SCH_RELATIONSHIP_TYPE', {
  StartToStart: 'SS',
  FinishToFinish: 'FF',
  FinishToStart: 'FS',
  StartToFinish: 'SF',
});

export type TaskRelationType = EnumSet<typeof TaskRelationType>;

export const ScheduleType = createEnum('SCH_TYPE', {
  Construction: 1,
  Sales: 2,
  PreConstruction: 3,
  Maintenance: 4,
});
export type ScheduleType = EnumSet<typeof ScheduleType>;

export const ScheduleTaskType = createEnum('SCH_TASK_TYPE', {
  CriticalPath: 1,
  Summary: 2,
  CostCenter: 3,
  Empty: 4,
});
export type ScheduleTaskType = EnumSet<typeof ScheduleTaskType>;

export const ScheduleTaskChangeDateAfterCompleteType = createEnum('SCH_CHANGE_DATE_AFTER_COMPLETE', {
  FinanceApproved: 1,
  PermitApproved: 2,
  AuthorityToCommence: 3,
  ContractDocsSigned: 4,
  ColorSelectionSigned: 5,
});
export type ScheduleTaskChangeDateAfterCompleteType = EnumSet<typeof ScheduleTaskChangeDateAfterCompleteType>;

export const ScheduleTaskTodoEventType = createEnum('SCH_TASK_TODO_EVENT', {
  BeforeStart: 1,
  AfterStart: 2,
  BeforeEnd: 3,
  AfterEnd: 4,
  AfterComplete: 5,
});
export type ScheduleTaskTodoEventType = EnumSet<typeof ScheduleTaskTodoEventType>;

export type ScheduleTemplateListItemDto = WithVersion<WithOptimisticLock<{
  id: ID;
  name: string;
  duration: number;
  isForWorksheet: boolean;
  active: ScheduleTemplateStatus;
  type: ScheduleType;
}>>;

export type ScheduleTemplateDetailsDto = WithRecalculationNeeded<WithVersion<{
  templateId?: ID;
  name?: string;
  duration: number;
  active: ScheduleTemplateStatus;
  type: ScheduleType;
  tasks: ScheduleTemplateTaskListItemDto[];
}>>;

export type ScheduleTemplateTaskListItemDto = WithVersion<{
  parentTaskNumber?: number;
  templateTaskId?: ID;
  name: string;
  criticalPathId?: ID;
  type: ScheduleTaskType;
  resource?: string;
  recentNote?: {
    memo: string;
    scheduleTaskNoteId: ID;
  };
  duration: number;
  taskNumber: number;
  changeDateAfterComplete: ScheduleTaskChangeDateAfterCompleteType;
  isSlab: boolean;
  isFromCriticalPath: boolean;
  isInSupervisorSchedule: boolean;
  isExcludeFromGanttChart: boolean;
  isResourceInUse: boolean;
  notes?: ScheduleTemplateTaskNoteListItemDto[];
  todo?: ScheduleTemplateTaskTodoListItemDto[];
  description?: string;
  costCenterId?: ID;
  predecessors?: TaskRelationDto[];
  supplierContactId?: ID;
  employeeContactId?: ID;
  startDate: Date;
  endDate: Date;
  mayHaveResource: boolean;
  markedAsDeleted?: boolean;
  canBeMarkedAsDeleted?: boolean;
}>;

export type ScheduleTemplateTaskDetailsDto = WithVersion<{
  ccResources: ScheduleTaskCcResourceListItemDto[];
  employeeResources: ScheduleTaskUserResourceListItemDto[];
  parentTaskNumber?: ID;
  templateTaskId?: ID;
  name: string;
  criticalPathId?: ID;
  type: ScheduleTaskType;
  resource?: string;
  duration?: number;
  taskNumber: number;
  changeDateAfterComplete: ScheduleTaskChangeDateAfterCompleteType;
  isSlab: boolean;
  isContractStart: boolean;
  isFromCriticalPath: boolean;
  isIncludedTasksExist: boolean;
  isInSupervisorSchedule: boolean;
  isExcludeFromGanttChart: boolean;
  notes?: ScheduleTemplateTaskNoteListItemDto[];
  todo?: ScheduleTemplateTaskTodoListItemDto[];
  description?: string;
  costCenterId?: ID;
  costCenterCode: number;
  predecessors?: TaskRelationDto[];
  sortOrder?: number;
  supplierContactId?: ID;
  employeeContactId?: ID;
  startDate?: Date;
  endDate: Date;
}>;

export type ScheduleTaskCcResourceListItemDto = {
  code: number;
  costCenterId: ID;
  isPreferred: boolean;
  name: string;
  supplierPhone: string;
  supplierContact: string;
  supplierName: string;
  supplierId: ID;
  currentTasks: ScheduleTaskRelatedResourceTaskListItemDto[];
};

export type ScheduleTaskUserResourceListItemDto = {
  userId: ID;
  name: string;
  currentTasks: ScheduleTaskRelatedResourceTaskListItemDto[];
};

export type ScheduleTaskRelatedResourceTaskListItemDto = {
  type: ScheduleTaskType;
  scheduleFor: string;
  name: string;
  startDate?: Date;
  endDate?: Date;
  duration?: number;
  completionPct?: number;
};

export type ScheduleTemplateTaskTodoListItemDto = {
  templateTaskTodoId?: ID;
  days: number;
  event?: ScheduleTaskTodoEventType;
  systemRole: UserSystemRole;
  addedDate: Date;
  memo?: string;
  user?: ScheduleTemplateTaskTodoUserAutocompleteDto;
};

export type ScheduleDetailsMasterInfoDto = {
  customerName: string;
  name: string;
  type: ScheduleType;
  jobId: ID;
  jobNumber: string;
  salesFolderId: ID;
  salesFolderName: string;
  salesFolderAddedDate: Date;
  startDate: Date;
  endDate: Date;
  duration: number;
  drawRuleId?: ID;
  drawRuleCode?: string;
  drawRuleName?: string;
  supervisorId: ID;
  supplierContactId: ID;
  supervisorName: string;
  baseStartDate: Date;
  baseEndDate: Date;
  isTodoExist: boolean;
  siteAddress: string;
  salespersonName: string;
  isSendReportToSupervisor: boolean;
  financeApprovedDate: Date;
  councilApprovedDate: Date;
  authToCommenceDate: Date;
  contractStartDate: Date;
  contractCompletionDate: Date;
  isDpp?: boolean;
  isOverridden?: boolean;
  customerId?: ID;
};

export type ScheduleDetailsTaskDto = WithVersion<{
  taskNumber: number;
  name: string;
  todo?: any[];
  type: ScheduleTaskType;
  notes?: any[];
  isSlab: boolean;
  endDate: Date;
  duration: number;
  parentTaskNumber?: number;
  resource?: string;
  sortOrder: number;
  startDate: Date;
  baseEndDate: Date;
  description?: string;
  costCenterId?: ID;
  costCenterCode?: string;
  orderNumber?: string;
  predecessors?: TaskRelationDto[];
  baseStartDate: Date;
  completionPct?: number;
  completionDate?: Date;
  confirmedOnDate?: Date;
  confirmedForDate?: Date;
  criticalPathId: ID;
  scheduleTaskId?: ID;
  isContractStart: boolean;
  supplierContactId?: ID;
  employeeContactId?: ID;
  recentNote?: { memo: string; scheduleTaskNoteId?: ID };
  isFromCriticalPath: boolean;
  isInSupervisorSchedule: boolean;
  isExcludeFromGanttChart: boolean;
  isResourceInUse: boolean;
  mayHaveResource: boolean;
}>;

export type ScheduleDetailsDto = WithVersion<ScheduleDetailsMasterInfoDto & {
  scheduleId: ID;
  templateId: ID;
  isFinalized: boolean;
  isNotProceeding: boolean;
  isSalesFolderActive: boolean;
  tasks: ScheduleDetailsTaskDto[];
  gDriveUrl: ID;
  jobContractConstructionPeriodIncludes: JobContractDaysPeriodType;
  templateVersion?: EntityVersion;
  errorMessage?: string;  
}>;

export type ScheduleTemplateListItemNoteDto = {
  templateTaskNoteId: ID;
  memo: string;
};

export type ScheduleTemplateTaskTodoUserAutocompleteDto = {
  userName: ID;
  userId: string;
};

export type ScheduleTemplateTaskNoteListItemDto = {
  templateTaskNoteId?: ID;
  memo?: string;
  addedDate: Date;
  addedBy?: string;
};

export type TaskRelationDto = {
  taskNo: number;
  relation: TaskRelationType;
  lag: number;
};

export const stringifyPredecessorRelationType = (type: TaskRelationType): string => {
  switch (type) {
    case TaskRelationType.FinishToFinish:
      return 'FF';
    case TaskRelationType.StartToFinish:
      return 'SF';
    case TaskRelationType.StartToStart:
      return 'SS';
    case TaskRelationType.FinishToStart:
      return 'FS';
    default:
      throw new Error(`Unknown task relation type: ${type}`);
  }
};

export const parsePredecessorRelationType = (type: string): TaskRelationType => {
  switch (type.toUpperCase()) {
    case 'FF':
      return TaskRelationType.FinishToFinish;
    case 'SF':
      return TaskRelationType.StartToFinish;
    case 'SS':
      return TaskRelationType.StartToStart;
    case 'FS':
    case '':
      return TaskRelationType.FinishToStart;
    default:
      throw new Error(`Unknown task relation type: ${type}`);
  }
};

export const stringifyPredecessor = ({ taskNo, relation, lag }: TaskRelationDto, dayStep: number = 8): string => {
  const hasTask = isNotNil(taskNo);
  const hasLag = isNotNil(lag) && lag !== 0;
  return [
    hasTask ? taskNo : '',
    hasTask ? stringifyPredecessorRelationType(relation) : '',
    hasTask && hasLag ? (lag > 0 ? '+' : '-') : '',
    hasTask && hasLag ? Math.abs(lag / dayStep) : '',
  ].join('');
};

export const stringifyPredecessors = (predecessors?: TaskRelationDto[], dayStep: number = 8): string =>
  isEmpty(predecessors) ?
    '' :
    // tslint:disable-next-line:no-non-null-assertion
    predecessors!.map((predecessor) => stringifyPredecessor(predecessor, dayStep)).join(', ');

export const isPredecessorComplete = ({ taskNo, relation, lag }: TaskRelationDto) => isNotNil(taskNo);

export const validatePredecessors = (predecessors?: TaskRelationDto[]): boolean =>
  // tslint:disable-next-line:no-non-null-assertion
  isEmpty(predecessors) ? true : predecessors!.every(isPredecessorComplete);

export const PREDECESSOR_RE = /^(\d+)(?:\s*)(FF|SS|FS|SF)?(?:\s*)([+-]\d+)?$/i;
export const parsePredecessors = (expression: string): Maybe<TaskRelationDto[]> => {
  // separate each relation
  const predecessorExpressions = expression.trim().split(/\s*,\s*/g).filter(Boolean);
  // verify that format of relation is correct
  const predecessorsMatches = <string[][]>predecessorExpressions.map((expr) => PREDECESSOR_RE.exec(expr));
  // if format is correct return set of relations
  return predecessorsMatches.some(isNil) ? void 0 : predecessorsMatches.map(([_, source, rel, lag]) => ({
    taskNo: Number(source),
    relation: parsePredecessorRelationType(rel || ''),
    lag: lag ? Number(lag) : 0,
  }));
};

export const validatePredecessorsFormat = (expression: string): boolean => {
  // separate each relation
  const predecessorExpressions = expression.trim().split(/\s*,\s*/g).filter(Boolean);
  // verify that format of relation is correct
  const predecessorsMatches = predecessorExpressions.map((expr) => PREDECESSOR_RE.test(expr));
  return !predecessorsMatches.find((match) => !match);
};

export const SCHEDULE_TYPE_CONSTRUCTION = 'Construction';
export const SCHEDULE_TYPE_SALES = 'Sales';
export const SCHEDULE_TYPE_PRE_CONSTRUCTION = 'PreConstruction';
export const SCHEDULE_TYPE_MAINTENANCE = 'Maintenance';

export const isTaskToStartRelationType = (type: TaskRelationType) =>
  type === TaskRelationType.StartToStart || type === TaskRelationType.FinishToStart;

export const isTaskToFinishRelationType = (type: TaskRelationType) =>
  type === TaskRelationType.StartToFinish || type === TaskRelationType.FinishToFinish;
